/**
 * The whole router, in one hook.
 *
 * Two routes - the dashboard and a single target's detail page - do not justify a
 * routing dependency, and hash routing (``#/target/<id>``) has a property that matters
 * more here than clean URLs: it needs no server rewrite. The detail page can be opened
 * in a fresh tab, deep-linked, and reloaded, and it still resolves, because everything
 * after the ``#`` never reaches FastAPI - which serves ``index.html`` and lets the app
 * read the fragment. A path-based route would 404 on reload unless the SPA fallback
 * caught it; the hash sidesteps that entirely.
 *
 * The target id contains colons and a URL (``whitehawk:manual:https://host/path``), so
 * it is percent-encoded in the hash and decoded back here - the raw id would otherwise
 * put a second ``:`` and ``//`` in the fragment and confuse anything parsing it.
 */

import { useCallback, useEffect, useState } from 'react'

export type Route =
  | { name: 'dashboard' }
  | { name: 'target'; targetId: string }
  | { name: 'login' }

function parse(hash: string): Route {
  // Tolerate "#/target/x", "#target/x" and a leading/trailing slash.
  const path = hash.replace(/^#/, '').replace(/^\/+/, '')
  const [section, ...rest] = path.split('/')
  if (section === 'login') return { name: 'login' }
  if (section === 'target' && rest.length > 0) {
    return { name: 'target', targetId: decodeURIComponent(rest.join('/')) }
  }
  return { name: 'dashboard' }
}

export function targetHref(targetId: string): string {
  return `#/target/${encodeURIComponent(targetId)}`
}

export function useHashRoute() {
  const [route, setRoute] = useState<Route>(() => parse(window.location.hash))

  useEffect(() => {
    const onChange = () => setRoute(parse(window.location.hash))
    window.addEventListener('hashchange', onChange)
    return () => window.removeEventListener('hashchange', onChange)
  }, [])

  /** Navigate within this tab (the browser Back button then works as expected). */
  const navigate = useCallback((route: Route) => {
    window.location.hash =
      route.name === 'target'
        ? targetHref(route.targetId)
        : route.name === 'login'
          ? '#/login'
          : '#/'
  }, [])

  return { route, navigate }
}
